'use client';

import React, { useState, useMemo } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, Search, Download } from 'lucide-react';
import type { TableColumn, TableSortState, PaginationState } from '@/types/dashboard';

interface DataTableProps {
  columns: TableColumn[];
  data: any[];
  pageSize?: number;
  isDark?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  onRowClick?: (row: any) => void;
  emptyMessage?: string;
  exportable?: boolean;
  exportFilename?: string;
}

export function DataTable({
  columns,
  data,
  pageSize = 20,
  isDark = true,
  searchable = true,
  searchPlaceholder = 'Search...',
  onRowClick,
  emptyMessage = 'No data available',
  exportable = true,
  exportFilename = 'export'
}: DataTableProps) {
  const [sortState, setSortState] = useState<TableSortState>({ key: null, direction: 'desc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    pageSize,
    totalItems: data.length
  });

  // Export to CSV function
  const exportToCSV = () => {
    const dataToExport = sortedData;
    const headers = columns.map(col => col.label).join(',');
    
    const rows = dataToExport.map(row => {
      return columns.map(col => {
        let value = row[col.key];
        
        if (col.formatter && value !== null && value !== undefined) {
          const formatted = col.formatter(value);
          if (typeof formatted === 'string') {
            const cleaned = formatted.replace(/[$,%]/g, '').trim();
            value = cleaned;
          } else {
            value = formatted;
          }
        }
        
        if (value === null || value === undefined) return '';
        
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        
        return stringValue;
      }).join(',');
    }).join('\n');
    
    const csv = `${headers}\n${rows}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${exportFilename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return data;

    const lowerSearch = searchTerm.toLowerCase();
    return data.filter(row =>
      Object.values(row).some(value =>
        String(value).toLowerCase().includes(lowerSearch)
      )
    );
  }, [data, searchTerm]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortState.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aVal = a[sortState.key!];
      const bVal = b[sortState.key!];

      // Handle null/undefined
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Numeric comparison
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortState.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // String comparison
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      
      if (sortState.direction === 'asc') {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });
  }, [filteredData, sortState]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, pagination]);

  // Calculate total pages
  const totalPages = Math.ceil(sortedData.length / pagination.pageSize);

  // Handle sort
  const handleSort = (columnKey: string, sortable: boolean = true) => {
    if (!sortable) return;

    setSortState(prev => ({
      key: columnKey,
      direction: prev.key === columnKey && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  // Reset to page 1 when search changes
  React.useEffect(() => {
    setPagination(prev => ({ ...prev, currentPage: 1, totalItems: filteredData.length }));
  }, [filteredData.length]);

  const getSortIcon = (columnKey: string, sortable: boolean = true) => {
    if (!sortable) return null;

    if (sortState.key !== columnKey) {
      return <ChevronsUpDown size={14} className="opacity-30" />;
    }

    return sortState.direction === 'asc' 
      ? <ChevronUp size={14} className="text-blue-500" />
      : <ChevronDown size={14} className="text-blue-500" />;
  };

  return (
    <div className="space-y-4">
      {/* Search Bar and Export Button */}
      {(searchable || exportable) && (
        <div className="flex items-center gap-3">
          {searchable && (
            <div className="relative flex-1">
              <Search
                className={`absolute left-3 top-2.5 ${isDark ? 'text-neutral-500' : 'text-gray-400'}`}
                size={16}
              />
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full border rounded-lg pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                  isDark
                    ? 'bg-neutral-900 border-neutral-800 text-white placeholder:text-neutral-600'
                    : 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-400'
                }`}
              />
            </div>
          )}
          
          {exportable && (
            <button
              onClick={exportToCSV}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                isDark
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
              title="Export to CSV"
            >
              <Download size={16} />
              <span>Export CSV</span>
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div className={`overflow-x-auto rounded-lg border ${isDark ? 'border-neutral-800' : 'border-gray-200'}`}>
        <table className="w-full">
          <thead>
            <tr className={`border-b ${isDark ? 'border-neutral-800 bg-neutral-900/50' : 'border-gray-200 bg-gray-50'}`}>
              {columns.map((column) => (
                <th
                  key={column.key}
                  onClick={() => handleSort(column.key, column.sortable)}
                  className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
                    column.sortable ? 'cursor-pointer hover:bg-opacity-70' : ''
                  } ${isDark ? 'text-neutral-400' : 'text-gray-600'} ${
                    column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : ''
                  }`}
                >
                  <div className={`flex items-center gap-1 ${
                    column.align === 'right' ? 'justify-end' : column.align === 'center' ? 'justify-center' : ''
                  }`}>
                    <span>{column.label}</span>
                    {getSortIcon(column.key, column.sortable)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td 
                  colSpan={columns.length}
                  className={`px-4 py-8 text-center text-sm ${isDark ? 'text-neutral-500' : 'text-gray-500'}`}
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              paginatedData.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  onClick={() => onRowClick?.(row)}
                  className={`border-b transition-colors ${
                    isDark 
                      ? 'border-neutral-800 hover:bg-neutral-900/30' 
                      : 'border-gray-200 hover:bg-gray-50'
                  } ${onRowClick ? 'cursor-pointer' : ''}`}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={`px-4 py-3 text-sm ${
                        isDark ? 'text-neutral-300' : 'text-gray-700'
                      } ${
                        column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : ''
                      }`}
                    >
                      {column.formatter 
                        ? column.formatter(row[column.key]) 
                        : row[column.key] ?? '-'
                      }
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={`flex items-center justify-between px-2 py-3 border-t ${
          isDark ? 'border-neutral-800' : 'border-gray-200'
        }`}>
          <div className={`text-sm ${isDark ? 'text-neutral-500' : 'text-gray-600'}`}>
            Showing {((pagination.currentPage - 1) * pagination.pageSize) + 1} to{' '}
            {Math.min(pagination.currentPage * pagination.pageSize, sortedData.length)} of{' '}
            {sortedData.length} results
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className={`px-3 py-1 text-sm rounded-md transition-all ${
                pagination.currentPage === 1
                  ? isDark 
                    ? 'bg-neutral-900 text-neutral-600 cursor-not-allowed' 
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : isDark
                  ? 'bg-neutral-800 text-white hover:bg-neutral-700'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Previous
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.currentPage <= 3) {
                  pageNum = i + 1;
                } else if (pagination.currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = pagination.currentPage - 2 + i;
                }

                return (
                  <button
                    key={i}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-8 h-8 text-sm rounded-md transition-all ${
                      pagination.currentPage === pageNum
                        ? 'bg-blue-600 text-white shadow-md'
                        : isDark
                        ? 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === totalPages}
              className={`px-3 py-1 text-sm rounded-md transition-all ${
                pagination.currentPage === totalPages
                  ? isDark 
                    ? 'bg-neutral-900 text-neutral-600 cursor-not-allowed' 
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : isDark
                  ? 'bg-neutral-800 text-white hover:bg-neutral-700'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
